import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Users,
  Briefcase,
  TrendingUp,
  ShieldAlert,
  Award,
} from 'lucide-react';
import PageHeader from '../../../components/common/PageHeader';
import StatsCard from '../../../components/common/StatsCard';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import StatusChip from '../../../components/common/StatusChip';
import LoadingSkeleton from '../../../components/common/LoadingSkeleton';
import ErrorState from '../../../components/common/ErrorState';
import MAMPerformanceChart from '../components/dashboard/MAMPerformanceChart';
import {
  mamManagerService,
  mamPerformanceService,
} from '../services/mamService';
import { formatCurrency, formatPercent } from '../utils';
import { MAM_ROUTES } from '../../../constants/routeConstants';
import type { MAMManager, MAMPerformancePoint } from '../types';

export default function MAMManagerDetails() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [manager, setManager] = useState<MAMManager | null>(null);
  const [perfData, setPerfData] = useState<MAMPerformancePoint[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        setError(null);
        const mgrRes = await mamManagerService.getById(id || 'mgr-101');
        const perfRes = await mamPerformanceService.getSeries();
        if (mgrRes) setManager(mgrRes);
        else setError('Manager not found');
        setPerfData(perfRes);
      } catch {
        setError('Error fetching manager details');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id]);

  if (loading) {
    return (
      <div className="space-y-6">
        <PageHeader title="Manager Details" />
        <LoadingSkeleton type="cards" cols={4} />
      </div>
    );
  }

  if (error || !manager) {
    return (
      <div className="space-y-4">
        <PageHeader title="Manager Details" />
        <ErrorState
          message={error || 'Manager not found'}
          onRetry={() => navigate(MAM_ROUTES.MAM_MANAGERS)}
        />
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-6 w-full">
      <div className="flex items-center space-x-3">
        <Button
          variant="outline"
          size="sm"
          onClick={() => navigate(MAM_ROUTES.MAM_MANAGERS)}
        >
          <ArrowLeft className="w-4 h-4 mr-1" /> Back to Managers
        </Button>
      </div>

      <PageHeader
        title={manager.name}
        description={`Account #${manager.accountNumber} • ${manager.email}`}
      >
        <StatusChip status={manager.status} size="lg" />
      </PageHeader>

      {/* Primary KPI Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatsCard
          title="Total AUM"
          value={formatCurrency(manager.totalAUM)}
          icon={Briefcase}
        />
        <StatsCard
          title="Total Return"
          value={formatPercent(manager.totalReturn)}
          icon={TrendingUp}
        />
        <StatsCard
          title="Active Sub-Accounts"
          value={manager.activeClients}
          icon={Users}
        />
        <StatsCard
          title="Max Drawdown"
          value={formatPercent(manager.maxDrawdown, false)}
          icon={ShieldAlert}
        />
      </div>

      {/* Main Grid: Details + Performance Chart */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Left Col: Overview Specs */}
        <div className="lg:col-span-5 space-y-6">
          <Card className="shadow-sm">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-semibold flex items-center gap-2">
                <Award className="w-4 h-4 text-indigo-600" />
                Manager Profile Specifications
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-xs">
              <div className="flex justify-between py-1.5 border-b border-border">
                <span className="text-muted-foreground">Manager ID</span>
                <span className="font-mono font-semibold">{manager.id}</span>
              </div>
              <div className="flex justify-between py-1.5 border-b border-border">
                <span className="text-muted-foreground">Monthly Return</span>
                <span className="font-bold text-emerald-600">
                  {formatPercent(manager.monthlyReturn)}
                </span>
              </div>
              <div className="flex justify-between py-1.5 border-b border-border">
                <span className="text-muted-foreground">Win Rate</span>
                <span className="font-bold text-emerald-600">
                  {manager.winRate}%
                </span>
              </div>
              <div className="flex justify-between py-1.5 border-b border-border">
                <span className="text-muted-foreground">Performance Fee</span>
                <span className="font-bold">{manager.performanceFee}%</span>
              </div>
              <div className="flex justify-between py-1.5 border-b border-border">
                <span className="text-muted-foreground">Management Fee</span>
                <span className="font-bold">{manager.managementFee}% p.a.</span>
              </div>
              <div className="flex justify-between py-1.5 border-b border-border">
                <span className="text-muted-foreground">Leverage</span>
                <span className="font-mono font-semibold">
                  {manager.leverage}
                </span>
              </div>
              <div className="flex justify-between py-1.5 border-b border-border">
                <span className="text-muted-foreground">Risk Level</span>
                <span className="font-semibold uppercase text-indigo-600">
                  {manager.riskLevel}
                </span>
              </div>
              <div className="flex justify-between py-1.5">
                <span className="text-muted-foreground">Trading Symbols</span>
                <div className="flex flex-wrap gap-1 justify-end max-w-[200px]">
                  {manager.tradingSymbols.map(sym => (
                    <span
                      key={sym}
                      className="px-1.5 py-0.5 bg-muted rounded font-mono text-[10px]"
                    >
                      {sym}
                    </span>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Right Col: Performance Chart */}
        <div className="lg:col-span-7">
          <MAMPerformanceChart
            data={perfData}
            title={`${manager.name} — Performance Growth`}
          />
        </div>
      </div>
    </div>
  );
}
